import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import * as i18next from 'i18next';
import * as api from 'core/api';
import * as utils from 'core/utils';
import * as flights from 'core/flights';
import { FlightStopKind } from 'core/api';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component
export default class FlightsBookingSidebarComponent extends Vue {
    @Prop()
    reservation: flights.ReservationModel;

    @Prop()
    reservationInfo: api.ReservationInfo;

    @Prop()
    step: string;

    get orderTitle(): string {
        return this.reservationInfo && i18next.t('label_order_your_with_number', <any>{ i: this.reservationInfo.reservationNumber });
    }

    get hasSelectedCustomOutwardBaggage(): boolean {
        return this.reservation.hasSelectedOutwardCustomBaggage
            || this.reservation.hasSelectedBothCustomBaggage;
    }

    get hasSelectedCustomReturnBaggage(): boolean {
        return this.reservation.hasSelectedReturnCustomBaggage
            || this.reservation.hasSelectedBothCustomBaggage;
    }

    isLegBaggageLabelNeeded(leg: flights.LegModel, legIndex: number) {
        const legHasNotBaggage = leg.baggage && leg.baggage.pieceWeight == 0 && leg.baggage.pieces == 0;
        const legHasAnyBaggage = (leg.baggage && leg.baggage.pieceWeight > 0)
                            || (leg.baggage && leg.baggage.pieces > 0);
        const legBaggageIsUnknown = !leg.baggage;

        return ((legHasNotBaggage || legBaggageIsUnknown) && legIndex == 0 && !this.hasSelectedCustomOutwardBaggage)
            || ((legHasNotBaggage || legBaggageIsUnknown) && legIndex == 1 && !this.hasSelectedCustomReturnBaggage)
            || legHasAnyBaggage;
    }

    isLegSeatsLabelNeeded(leg: flights.LegModel){
        if(Object.values(this.reservation.flatSelectedSeats).length){
            return leg.segments.some(segment=>{
                return this.reservation.flatSelectedSeats[segment.flightCode]?.length;
            });
        }
        return false;
    }

    isLegCustomBaggageLabelNeeded(leg: flights.LegModel, legIndex: number) {
        return (legIndex == 0 && this.hasSelectedCustomOutwardBaggage)
            || (legIndex == 1 && this.hasSelectedCustomReturnBaggage);
    }

    isSegmentBaggageLabelNeeded(segment: flights.SegmentModel, segmentsBunch: flights.SegmentModel[]) {
        const segmentHasNotBaggage = segment.baggage && segment.baggage.pieceWeight == 0 && segment.baggage.pieces == 0;
        const segmentHasAnyBaggage = (segment.baggage && segment.baggage.pieceWeight > 0)
            || (segment.baggage && segment.baggage.pieces > 0);
        const segmentBaggageIsUnknown = !segment.baggage;

        return ((segmentHasNotBaggage || segmentBaggageIsUnknown)
            && !this.reservation.hasSelectedCustomBaggageBySegmentsBunch(segmentsBunch))
                || segmentHasAnyBaggage;
    }

    isSegmentCustomBaggageLabelNeeded(segmentsBunch: flights.SegmentModel[]) {
        return this.reservation.hasSelectedCustomBaggageBySegmentsBunch(segmentsBunch);
    }

    getBaggageLabel(segment: flights.SegmentModel, isCabin: boolean): string {
        // For multiFR display baggage from brand group selected flight
        if (this.reservation.hasBrandGroups) {
            this.reservation.brandGroups.forEach((brandGroup) => {
                const f = brandGroup.selectedFlight;

                if (f?.legs[0].segments[0].departureDestination.uniqueId == segment.departureDestination.uniqueId
                    && f?.legs[0].segments[0].arrivalDestination.uniqueId == segment.arrivalDestination.uniqueId) {
                    segment = f?.legs[0].segments[0];
                }
                else {
                    // Check if return leg for CombiIncut
                    if (this.reservation.defaultFlight?.multiFRType == 5) {
                        if (f?.legs[1].segments[0].departureDestination.uniqueId == segment.arrivalDestination.uniqueId
                            && f?.legs[1].segments[0].arrivalDestination.uniqueId == segment.departureDestination.uniqueId) {
                            segment = f?.legs[0].segments[0];
                        }
                    }
                }
            });
        }

        return utils.Baggage.getLabel(isCabin ? segment.baggageCabin : segment.baggage, isCabin);
    }

    getBaggageLabelCombi(segmentsBunch: flights.SegmentModel[], isCabin: boolean): string {
        let segment = segmentsBunch[0];

        // For multiFR display baggage from brand group selected flight
        if (this.reservation.hasBrandGroups) {
            this.reservation.brandGroups.forEach((brandGroup) => {
                brandGroup.selectedFlight?.legs.forEach((leg) => {
                    if (leg.departureDestination.uniqueId === segmentsBunch[0].departureDestination.uniqueId
                        && leg.arrivalDestination.uniqueId === segmentsBunch[segmentsBunch.length - 1].arrivalDestination.uniqueId) {
                        segment = leg.segments[0];
                    }
                });
            });
        }

        return utils.Baggage.getLabel(isCabin ? segment.baggageCabin : segment.baggage, isCabin);
    }

    getBrandLabel(segment: flights.SegmentModel | null = null): string {
        let result = '';

        // For multiFR display baggage from brand group selected flight
        if (this.reservation.hasBrandGroups && segment) {
            this.reservation.brandGroups.forEach((brandGroup) => {
                const f = brandGroup.selectedFlight;

                if (f?.legs[0].segments[0].departureDestination.uniqueId == segment.departureDestination.uniqueId
                    && f?.legs[0].segments[0].arrivalDestination.uniqueId == segment.arrivalDestination.uniqueId) {
                    if (f?.brand
                        && f?.brand.nameUI) {
                        result = f.brand.nameUI;
                    }
                }
            });
        } else {
            if (this.reservation.selectedFlight
                && this.reservation.selectedFlight.brand
                && this.reservation.selectedFlight.brand.nameUI) {
                result = this.reservation.selectedFlight.brand.nameUI;
            }
        }

        return result ? ', ' + result : result;
    }

    getBookingClassLabel(bookingClass: string): string {
        return i18next.t('label_flight_class_' + bookingClass.toLowerCase());
    }

    getDestinationTooltip(d: api.Destination): string {
        const result = d.localizedName + '<br/>' +
            '<i class="el-icon-location"></i> ' + d.city.localizedName + ', ' +
            d.country.localizedName;

        return result;
    }

    getLegMaintenanceStops(leg: flights.LegModel) {
        let stops: flights.SegmentStopModel[] = [];

        leg.segments.forEach(segment => {
            if (segment.stops && segment.stops.length > 0) {
                stops = stops.concat(segment.stops);
            }
        });

        return stops;
    }

    getMaintenanceStopsDelimeter(leg: flights.LegModel, maintenanceStopIndex: number) {
        const legMaintenanceStops = this.getLegMaintenanceStops(leg);

        return legMaintenanceStops.length > 1 && maintenanceStopIndex + 1 != legMaintenanceStops.length ? ',' : '';
    }

    onFlightDetails() {
        this.$emit('details');
    }
}
